import type { CartQuoteResponse } from "@pufimi/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useCartStore } from "@/stores/useCartStore";
import { cartQuoteQuery } from "./cartQuote";

export interface UseCartQuote {
  /** The server-authoritative quote, or null while loading / on error. */
  quote: CartQuoteResponse | null;
  /** The quote could not be fetched at all (network / 5xx, after the retries gave up). */
  isError: boolean;
  refetch: () => void;
  /** At least one line was dropped from the local cart this visit (show the notice). */
  expired: boolean;
}

// The cart quote PLUS the self-heal that has to accompany it (ADR-0054 §2). The
// browser-local cart outlives both the 7-day GC window and the 7-day anonymous session
// cookie, so a returning buyer's cart routinely references Products the server will no
// longer quote. The server reports those ids in `unavailableProductIds` instead of
// refusing the cart; this hook is the client half of that contract: it removes them from
// the local cart — which changes the query key, so the remaining lines re-quote on their
// own — and raises `expired` once, so the surface can say what happened.
//
// Shared by the mini-cart and the checkout page so the cart heals wherever the buyer
// happens to be looking, and the nav badge stops counting products that are gone.
export function useCartQuote({ enabled = true }: { enabled?: boolean } = {}): UseCartQuote {
  const items = useCartStore((s) => s.items);
  const removeProduct = useCartStore((s) => s.removeProduct);
  const {
    data = null,
    isError,
    refetch,
  } = useQuery({ ...cartQuoteQuery(items), enabled: enabled && items.length > 0 });

  // Ids already pruned, so a re-render (or the previous-data window held during the
  // re-quote) can't drive a second removal or re-raise the notice. Render never reads it.
  const prunedIds = useRef<Set<string>>(new Set());
  const [expired, setExpired] = useState(false);
  const unavailable = data?.unavailableProductIds;

  useEffect(() => {
    if (!unavailable?.length) return;
    const fresh = unavailable.filter((id) => !prunedIds.current.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) {
      prunedIds.current.add(id);
      removeProduct(id);
    }
    setExpired(true);
  }, [unavailable, removeProduct]);

  return { quote: data, isError, refetch: () => void refetch(), expired };
}
